import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Tag, Select, Button, Space, Typography, Input, InputNumber, DatePicker,
  Modal, Form, Popconfirm, message, Tooltip, List, Divider,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
  UserOutlined, TeamOutlined, ThunderboltOutlined, DownOutlined, RightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { Action, Task, TaskStatus, CreateTaskPayload, UpdateTaskPayload, Ticket } from '../../types/triage';
import { createTask, updateTask, deleteTask, fetchActions, createAction } from '../../config/api';

dayjs.extend(relativeTime);

const STATUS_COLORS: Record<string, string> = {
  'pending': 'blue',
  'in-progress': 'orange',
  'accepted': 'cyan',
  'done': 'green',
  'canceled': 'default',
  'auto-resolved': 'purple',
};

const EXECUTOR_OPTIONS = [
  { value: 'Contractor', label: 'Contractor' },
  { value: 'Hotel Engineering', label: 'Hotel Engineering' },
  { value: 'Project coordinator', label: 'Project Coordinator' },
];

const STATUS_LABELS: Record<string, string> = {
  'pending': 'Pending',
  'in-progress': 'In Progress',
  'accepted': 'Accepted',
  'done': 'Done',
  'canceled': 'Canceled',
  'auto-resolved': 'Auto-Resolved',
};

const OPEN_STATUSES = new Set<TaskStatus>(['pending', 'in-progress', 'accepted']);

interface TaskSectionProps {
  ticket: Ticket;
  tasks: Task[];
  onTaskCreated: (task: Task) => void;
  onTaskUpdated: (task: Task) => void;
  onTaskDeleted: (taskId: string) => void;
  // When set, new tasks are created under this issue_id instead of a support_ticket_id.
  // The ticket prop is still used for display fields (observable, action defaults, etc.).
  issueId?: number;
}

const TaskSection: React.FC<TaskSectionProps> = ({
  ticket, tasks, onTaskCreated, onTaskUpdated, onTaskDeleted, issueId,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const { openTasks, closedTasks } = useMemo(() => {
    const open: Task[] = [];
    const closed: Task[] = [];
    for (const t of tasks) {
      if (OPEN_STATUSES.has(t.status)) open.push(t);
      else closed.push(t);
    }
    return { openTasks: open, closedTasks: closed };
  }, [tasks]);

  // --- Actions list ---
  const [actions, setActions] = useState<Action[]>([]);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [creatingAction, setCreatingAction] = useState(false);
  const [actionForm] = Form.useForm();

  useEffect(() => {
    fetchActions().then(setActions).catch(() => {});
  }, []);

  const handleCreateAction = async (values: any) => {
    if (!values.action?.trim()) return;
    setCreatingAction(true);
    try {
      const created = await createAction({
        action: values.action.trim(),
        instruction_link: values.instruction_link?.trim() || null,
        time_allocated_internal_mins: values.time_allocated_internal_mins ?? null,
        time_allocated_external_mins: values.time_allocated_external_mins ?? null,
        equipment: Array.isArray(values.equipment) ? values.equipment : [],
      });
      setActions((prev) => {
        const without = prev.filter((a) => a.id !== created.id);
        return [...without, created].sort((a, b) => a.action.localeCompare(b.action));
      });
      form.setFieldValue('action_id', created.id);
      editForm.setFieldValue('action_id', created.id);
      actionForm.resetFields();
      setActionModalOpen(false);
      message.success('Action saved');
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setCreatingAction(false);
    }
  };

  // Dropdown footer: button to open the full new-action modal
  const actionDropdownRender = (menu: React.ReactNode) => (
    <>
      {menu}
      <Divider style={{ margin: '8px 0' }} />
      <div style={{ padding: '0 8px 4px' }}>
        <Button
          size="small"
          type="text"
          icon={<PlusOutlined />}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setActionModalOpen(true)}
        >
          New action…
        </Button>
      </div>
    </>
  );

  // --- Create Task ---
  const handleCreate = async (values: any) => {
    setCreating(true);
    try {
      const payload: CreateTaskPayload = {
        ...(issueId != null ? { issue_id: issueId } : { support_ticket_id: ticket.ticket_id }),
        title: values.title,
        description: values.description || undefined,
        due_date: values.due_date ? values.due_date.toISOString() : undefined,
        action_id: values.action_id ?? ticket.action_id ?? undefined,
        executor: values.executor || undefined,
        parent_entity_type: 'node_to_slot_mapping',
        parent_entity_id: ticket.observable_id,
      };
      const created = await createTask(payload);
      onTaskCreated(created);
      setCreateModalOpen(false);
      form.resetFields();
      message.success('Task created');
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  // --- Update Status inline ---
  const handleStatusChange = async (task: Task, newStatus: TaskStatus) => {
    try {
      const updated = await updateTask(task.id, { status: newStatus });
      onTaskUpdated(updated);
    } catch (err: any) {
      message.error(err.message);
    }
  };

  // --- Edit Task ---
  const handleEditSave = async (values: any) => {
    if (!editingTask) return;
    setSaving(true);
    try {
      const payload: UpdateTaskPayload = {
        title: values.title,
        due_date: values.due_date ? values.due_date.toISOString() : null,
        description: values.description || null,
        executor: values.executor || null,
      };
      const updated = await updateTask(editingTask.id, payload);
      onTaskUpdated(updated);
      setEditingTask(null);
      message.success('Task updated');
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // --- Delete Task ---
  const handleDelete = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      onTaskDeleted(taskId);
      message.success('Task deleted');
    } catch (err: any) {
      // Server requires admin/fleet-ops for task deletion (PR2 RBAC).
      // Surface a friendly message instead of the raw 'forbidden'.
      if (err?.status === 403) {
        message.error('Delete requires the admin or fleet-ops role.');
      } else {
        message.error(err.message);
      }
    }
  };

  // --- Default title suggestion ---
  const suggestedTitle = [
    ticket.action_name,
    ticket.observable_name || ticket.observable_id,
  ].filter(Boolean).join(' — ');

  // Set initial values AFTER the modal mounts (Form attaches on render).
  // Calling setFieldsValue from the open-click handler races antd's
  // useForm bind and silently drops the prefilled values.
  useEffect(() => {
    if (createModalOpen) {
      form.setFieldsValue({
        title: suggestedTitle,
        action_id: ticket.action_id ?? undefined,
        executor: 'Contractor',
      });
    }
  }, [createModalOpen, suggestedTitle, ticket.action_id, form]);

  const renderTask = (task: Task, dimmed = false) => (
    <List.Item
      style={{ padding: '8px 0', opacity: dimmed ? 0.65 : 1 }}
      actions={[
        <Select
          key="status"
          value={task.status}
          onChange={(val) => handleStatusChange(task, val)}
          size="small"
          style={{ width: 120 }}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'in-progress', label: 'In Progress' },
            { value: 'accepted', label: 'Accepted' },
            { value: 'done', label: 'Done' },
            { value: 'canceled', label: 'Canceled' },
          ]}
        />,
        <Tooltip key="edit" title="Edit">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              editForm.setFieldsValue({
                title: task.title,
                due_date: task.due_date ? dayjs(task.due_date) : null,
                description: task.description,
                action_id: task.action_id,
                executor: task.executor,
              });
              setEditingTask(task);
            }}
          />
        </Tooltip>,
        <Popconfirm key="delete" title="Delete this task?" onConfirm={() => handleDelete(task.id)} okText="Delete" okButtonProps={{ danger: true }}>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>,
      ]}
    >
      <List.Item.Meta
        title={
          <Space size={4}>
            <Typography.Text style={{ fontSize: 13 }}>{task.title}</Typography.Text>
            <Tag color={STATUS_COLORS[task.status] || 'default'}>{STATUS_LABELS[task.status] || task.status}</Tag>
            {task.action_name && <Tag icon={<ThunderboltOutlined />} color="geekblue">{task.action_name}</Tag>}
          </Space>
        }
        description={
          <Space size={16} style={{ fontSize: 12 }}>
            {task.claimer_email && <span><TeamOutlined style={{ marginRight: 2 }} />{task.claimer_email}</span>}
            {task.executor && <span><UserOutlined style={{ marginRight: 2 }} />{task.executor}</span>}
            {task.due_date && <span>Due: {dayjs(task.due_date).format('MMM D, YYYY')}</span>}
            <span>{dayjs(task.created_at).fromNow()}</span>
          </Space>
        }
      />
    </List.Item>
  );

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <CheckCircleOutlined />
          <span>Tasks</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {openTasks.length} open
            {closedTasks.length > 0 && ` · ${closedTasks.length} closed`}
          </Typography.Text>
        </Space>
      }
      extra={
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => {
            // Open the modal first so the <Form> mounts and antd's useForm
            // instance attaches; setFieldsValue before mount warns and
            // silently drops the values (the prefilled title/action/executor).
            // See the ActionsPage useEffect(modalOpen,…) pattern for the
            // same pattern in the canonical location.
            setCreateModalOpen(true);
          }}>
            Create Task
          </Button>
      }
    >
      {tasks.length === 0 ? (
        <Typography.Text type="secondary">No tasks for this ticket</Typography.Text>
      ) : (
        <>
          {openTasks.length > 0 ? (
            <List
              dataSource={openTasks}
              size="small"
              renderItem={(task) => renderTask(task, false)}
            />
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              No open tasks
            </Typography.Text>
          )}

          {closedTasks.length > 0 && (
            <>
              <div
                onClick={() => setShowClosed((v) => !v)}
                style={{
                  cursor: 'pointer',
                  marginTop: 8,
                  padding: '6px 0',
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: '#8c8c8c',
                  fontWeight: 500,
                }}
              >
                {showClosed ? <DownOutlined /> : <RightOutlined />}
                Closed tasks ({closedTasks.length})
              </div>
              {showClosed && (
                <List
                  dataSource={closedTasks}
                  size="small"
                  renderItem={(task) => renderTask(task, true)}
                />
              )}
            </>
          )}
        </>
      )}

      {/* Create Modal */}
      <Modal
        title="Create Task"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder="Task title" />
          </Form.Item>
          <Form.Item name="action_id" label="Action" initialValue={ticket.action_id}>
            <Select
              placeholder="Select an action"
              allowClear
              showSearch
              optionFilterProp="label"
              dropdownRender={actionDropdownRender}
              options={actions.map((a) => ({ value: a.id, label: a.action }))}
            />
          </Form.Item>
          <Form.Item name="executor" label="Executor" initialValue="Contractor">
            <Select options={EXECUTOR_OPTIONS} placeholder="Select executor" />
          </Form.Item>
          <Form.Item name="due_date" label="Due Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Task description..." />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creating}>Create</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Task"
        open={!!editingTask}
        onCancel={() => setEditingTask(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="action_id" label="Action">
            <Select
              placeholder="Select an action"
              allowClear
              showSearch
              optionFilterProp="label"
              disabled
              options={actions.map((a) => ({ value: a.id, label: a.action }))}
            />
          </Form.Item>
          <Form.Item name="executor" label="Executor">
            <Select options={EXECUTOR_OPTIONS} placeholder="Select executor" />
          </Form.Item>
          <Form.Item name="due_date" label="Due Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditingTask(null)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* New/Edit Action Modal */}
      <Modal
        title="New action"
        open={actionModalOpen}
        onCancel={() => { setActionModalOpen(false); actionForm.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={actionForm} layout="vertical" onFinish={handleCreateAction}>
          <Form.Item
            name="action"
            label="Name"
            rules={[{ required: true, message: 'Action name is required' }]}
          >
            <Input placeholder="e.g. Replace Node" autoFocus />
          </Form.Item>
          <Form.Item
            name="instruction_link"
            label="Instruction link"
            rules={[{ type: 'url', message: 'Must be a valid URL', warningOnly: true }]}
          >
            <Input placeholder="https://…" />
          </Form.Item>
          <Space.Compact block>
            <Form.Item
              name="time_allocated_internal_mins"
              label="Internal time (min)"
              style={{ flex: 1, marginRight: 8 }}
            >
              <InputNumber min={0} step={5} style={{ width: '100%' }} placeholder="e.g. 15" />
            </Form.Item>
            <Form.Item
              name="time_allocated_external_mins"
              label="External time (min)"
              style={{ flex: 1 }}
            >
              <InputNumber min={0} step={5} style={{ width: '100%' }} placeholder="e.g. 30" />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="equipment" label="Equipment">
            <Select
              mode="tags"
              placeholder="Type and press Enter to add equipment"
              tokenSeparators={[',']}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
            If the action already exists (by name), these fields will update the existing record.
          </Typography.Text>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setActionModalOpen(false); actionForm.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creatingAction}>Save action</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default TaskSection;
